<!-- 
Dialog for editing non-compliance history configuration for a levy collection.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Configure non-compliance history</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="configure-non-compliance-history"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12>
                <v-select
                  :value="configuration.enableSanctions"
                  :items="lookups.yesNo"
                  label="Include sanctions"
                  @input="update('enableSanctions', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <v-select
                  :value="configuration.enableShortRegistrationPeriodRiskFactor"
                  :items="lookups.yesNo"
                  label="Include registration periods"
                  @input="update('enableShortRegistrationPeriodRiskFactor', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <v-select
                  :value="configuration.numberOfAnnualChargeLatePaymentYears"
                  :items="years"
                  label="Include annual charge late payments"
                  @input="update('numberOfAnnualChargeLatePaymentYears', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex xs12>
                <v-select
                  :value="configuration.numberOfLtpsLatePaymentYears"
                  :items="years"
                  label="Include levy late payments"
                  @input="update('numberOfLtpsLatePaymentYears', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <template v-if="showWeighting(1)">
                <v-flex xs12>
                  <h3 class="subheading mb-3 mt-4">Late payment measure weightings</h3>
                </v-flex>
                <v-flex xs12 md4>
                  <v-text-field 
                    :value="configuration.latePaymentWeightingForPreviousYear" 
                    :rules="[rules.required,rules.number,rules.betweenZeroAndOne]"
                    label="Weighting for previous year*" 
                    @input="update('latePaymentWeightingForPreviousYear', $event)"
                  />
                </v-flex>
                <v-flex v-if="showWeighting(2)" xs12 md4>
                  <v-text-field 
                    :value="configuration.latePaymentWeightingForPreviousYearMinus1" 
                    :rules="[rules.required,rules.number,rules.betweenZeroAndOne]"
                    label="Weighting for previous year - 1*" 
                    @input="update('latePaymentWeightingForPreviousYearMinus1', $event)"
                  />
                </v-flex>
                <v-flex v-if="showWeighting(3)" xs12 md4>
                  <v-text-field 
                    :value="configuration.latePaymentWeightingForPreviousYearMinus2" 
                    :rules="[rules.required,rules.number,rules.betweenZeroAndOne]"
                    label="Weighting for previous year - 2*" 
                    @input="update('latePaymentWeightingForPreviousYearMinus2', $event)"
                  />
                </v-flex>
                <v-flex v-if="showWeighting(4)" xs12 md4>
                  <v-text-field 
                    :value="configuration.latePaymentWeightingForPreviousYearMinus3" 
                    :rules="[rules.required,rules.number,rules.betweenZeroAndOne]"
                    label="Weighting for previous year - 3*" 
                    @input="update('latePaymentWeightingForPreviousYearMinus3', $event)"
                  />
                </v-flex>
                <v-flex v-if="showWeighting(5)" xs12 md4>
                  <v-text-field 
                    :value="configuration.latePaymentWeightingForPreviousYearMinus4" 
                    :rules="[rules.required,rules.number,rules.betweenZeroAndOne]"
                    label="Weighting for previous year - 4*" 
                    @input="update('latePaymentWeightingForPreviousYearMinus4', $event)"
                  />
                </v-flex>
              </template>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-alert :value="!!warning" type="info">
          {{ warning }}
        </v-alert>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="primary" 
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error"/>
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.editNonComplianceHistoryConfiguration;
    },
    configuration() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    years() {
      return [
        { text: "No", value: 0 },
        { text: "From previous year only", value: 1 },
        { text: "From previous 2 years", value: 2 },
        { text: "From previous 3 years", value: 3 },
        { text: "From previous 4 years", value: 4 },
        { text: "From previous 5 years", value: 5 }
      ];
    },
    warning() {
      const sanctions = this.configuration.enableSanctions;
      const shortRegistrationPeriodRiskFactor = this.configuration.enableShortRegistrationPeriodRiskFactor;
      const annualCharge = this.configuration.numberOfAnnualChargeLatePaymentYears > 0;
      if (sanctions && shortRegistrationPeriodRiskFactor && annualCharge) {
        return "Provider sanctions, short registration periods, and annual charge late payment details will need to be uploaded along with the offline levy parameters.";
      }
      if (sanctions && annualCharge) {
        return "Provider sanctions and annual charge late payment details will need to be uploaded along with the offline levy parameters.";
      }
      if (sanctions && shortRegistrationPeriodRiskFactor) {
        return "Provider sanctions and short registration periods will need to be uploaded along with the offline levy parameters.";
      }
      if (shortRegistrationPeriodRiskFactor && annualCharge) {
        return "Provider short registration periods and annual charge late payment details will need to be uploaded along with the offline levy parameters.";
      }
      if (sanctions) {
        return "Provider sanctions will need to be uploaded along with the offline levy parameters.";
      }
      if (shortRegistrationPeriodRiskFactor) {
        return "Provider short registration periods will need to be uploaded along with the offline levy parameters.";
      }
      if (annualCharge) {
        return "Annual charge late payment details will need to be uploaded along with the offline levy parameters.";
      }
      return null;
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.dispatch(action.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET, this.levyCollectionId);
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (
        this.$refs.form.validate() &&
        (await this.$store.dispatch(action.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE))
      ) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    showWeighting(number) {
      return (
        this.configuration.numberOfAnnualChargeLatePaymentYears >= number ||
        this.configuration.numberOfLtpsLatePaymentYears >= number
      );
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
